import { useMemo, useRef } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  createShopOrderCsMemo,
  deleteShopOrderCsMemo,
  getShopOrderCsMemoLog,
  getShopOrderCsMemoLogAll,
  getShopOrderCsMemos,
  getShopOrderCsMemosBatch,
  isShopCsMemoNotFound,
} from './shopOrderCsMemo.controller';
import {
  ShopOrderCsMemo,
  ShopOrderCsMemoLogItem,
  ShopOrderCsMemoLogParams,
  ShopOrderCsMemoLogResponse,
  ShopOrderCsMemoTarget,
} from './shopOrderCsMemo.interface';

/**
 * 조회는 전용 엔드포인트로만 한다 (csMemos virtual populate 미사용).
 * 이유는 gift와 동일 — 구서버에서 StrictPopulateError 500이 나고, adminOnly가 안 실린다.
 */
export const shopOrderCsMemoQueryKey = (shopOrder: string | undefined): string[] => [
  'shop-order-cs-memo',
  'order',
  shopOrder ?? '',
];

const shopOrderCsMemoBulkQueryKey = (orderIds: string[]): string[] => [
  'shop-order-cs-memo',
  'orders',
  [...orderIds].sort().join(','),
];

/** 목록 화면에서 한 번에 채울 주문 수 상한 (배치 100건씩 → 요청 3건까지) */
export const SHOP_CS_MEMO_BULK_LIMIT = 300;
const SHOP_CS_MEMO_BATCH_LIMIT = 100;

const fetchOrNull = async (
  shopOrder: string,
): Promise<ShopOrderCsMemo[] | null> => {
  try {
    return await getShopOrderCsMemos(shopOrder);
  } catch (e) {
    if (isShopCsMemoNotFound(e)) return null; // 엔드포인트 미배포
    throw e;
  }
};

/** 주문 단위 타임라인. `null` = 미배포(404), `[]` = 메모 없음 */
export const useShopOrderCsMemoListQuery = (
  target: ShopOrderCsMemoTarget,
  enabled = true,
) => {
  const shopOrder = target.shopOrder;
  return useQuery<ShopOrderCsMemo[] | null>(
    shopOrderCsMemoQueryKey(shopOrder),
    () => fetchOrNull(shopOrder ?? ''),
    { enabled: enabled && !!shopOrder, staleTime: 30 * 1000, retry: 1 },
  );
};

/** 조회 없이 캐시만 구독 (카드·배지 개수 동기화용) */
export const useShopOrderCsMemoCache = (
  target: ShopOrderCsMemoTarget,
): ShopOrderCsMemo[] | null | undefined =>
  useQuery<ShopOrderCsMemo[] | null>(
    shopOrderCsMemoQueryKey(target.shopOrder),
    () => fetchOrNull(target.shopOrder ?? ''),
    { enabled: false },
  ).data;

/**
 * 배치 조회에 쓸 주문 id를 안정적으로 유지한다.
 * 셀러 홈·주문배송관리는 상단 검색어로 목록이 한 글자마다 좁혀지는데, 그대로 키에 쓰면
 * 타이핑할 때마다 배치 요청이 새로 나간다. 이미 받아 둔 주문은 그대로 두고 새로 등장한
 * 주문만 합쳐서 — 부분집합이면 키가 그대로라 재요청이 없다.
 */
export const useStableCsMemoOrderIds = (orderIds: string[]): string[] => {
  const seenRef = useRef<string[]>([]);
  return useMemo(() => {
    const known = new Set(seenRef.current);
    const fresh = orderIds.filter((id) => id && !known.has(id));
    if (fresh.length === 0) return seenRef.current;
    // 상한을 넘으면 최근에 등장한 주문을 남긴다 (오래된 조회분이 새 주문을 밀어내지 않게)
    seenRef.current = [...seenRef.current, ...fresh].slice(-SHOP_CS_MEMO_BULK_LIMIT);
    return seenRef.current;
  }, [orderIds]);
};

export interface ShopOrderCsMemosByOrderResult {
  memoMap: Map<string, ShopOrderCsMemo[]>;
  isLoading: boolean;
  /** true = 백엔드 미배포(404) */
  unavailable: boolean;
}

/**
 * 카드·목록 화면용 — 배치 1요청으로 여러 주문의 메모를 채우고 개별 캐시에도 심는다.
 * (셀러가 볼 수 없는 주문은 서버가 빈 배열로 준다 — 403으로 화면이 깨지지 않는다)
 */
export const useShopOrderCsMemosByOrders = (
  orderIds: string[],
  enabled = true,
): ShopOrderCsMemosByOrderResult => {
  const queryClient = useQueryClient();
  const capped = orderIds.filter(Boolean).slice(0, SHOP_CS_MEMO_BULK_LIMIT);

  const { data, isLoading } = useQuery<Record<string, ShopOrderCsMemo[]> | null>(
    shopOrderCsMemoBulkQueryKey(capped),
    async () => {
      let merged: Record<string, ShopOrderCsMemo[]> | null = null;
      try {
        const acc: Record<string, ShopOrderCsMemo[]> = {};
        for (let i = 0; i < capped.length; i += SHOP_CS_MEMO_BATCH_LIMIT) {
          const chunk = capped.slice(i, i + SHOP_CS_MEMO_BATCH_LIMIT);
          Object.assign(acc, await getShopOrderCsMemosBatch(chunk));
        }
        merged = acc;
      } catch (e) {
        if (!isShopCsMemoNotFound(e)) throw e;
        merged = null; // 미배포 — 카드에서 메모 UI를 조용히 접는다
      }

      if (merged) {
        for (const [orderId, memos] of Object.entries(merged)) {
          queryClient.setQueryData(shopOrderCsMemoQueryKey(orderId), memos);
        }
      } else {
        // 미배포(404)면 개별 캐시에도 null을 심는다 — 카드 메모 블록이 스스로 접혀,
        // 저장이 안 되는 입력창을 담당자에게 보여주지 않는다.
        for (const orderId of capped) {
          queryClient.setQueryData(shopOrderCsMemoQueryKey(orderId), null);
        }
      }
      return merged;
    },
    // retry 1 — 메모는 부가 정보라 서버가 흔들릴 때 요청을 불리지 않는다(기본 3회)
    { enabled: enabled && capped.length > 0, staleTime: 30 * 1000, retry: 1 },
  );

  const memoMap = useMemo(() => {
    const map = new Map<string, ShopOrderCsMemo[]>();
    if (data) {
      for (const [orderId, memos] of Object.entries(data)) map.set(orderId, memos);
    }
    return map;
  }, [data]);

  return { memoMap, isLoading, unavailable: data === null };
};

/** CS 응대 로그 목록. `null` = 미배포(404) */
export const useShopOrderCsMemoLogQuery = (
  params: ShopOrderCsMemoLogParams,
  enabled = true,
) =>
  useQuery<ShopOrderCsMemoLogResponse | null>(
    ['shop-order-cs-memo', 'log', JSON.stringify(params)],
    async () => {
      try {
        return await getShopOrderCsMemoLog(params);
      } catch (e) {
        if (isShopCsMemoNotFound(e)) return null;
        throw e;
      }
    },
    { enabled, keepPreviousData: true, staleTime: 30 * 1000 },
  );

export interface ShopCsMemoLogAllResult {
  items: ShopOrderCsMemoLogItem[];
  total: number;
  /** true = 상한(2000건)에 걸려 일부만 집계 */
  capped: boolean;
}

/**
 * 조회 조건 전체 수집 (통계 패널용). 무거우니 **패널을 열 때만** 돈다(enabled).
 * 표는 기존 페이지 조회 그대로 쓴다.
 */
export const useShopOrderCsMemoLogAllQuery = (
  params: ShopOrderCsMemoLogParams,
  enabled: boolean,
) => {
  const base: ShopOrderCsMemoLogParams = {
    ...(params.shopOrder ? { shopOrder: params.shopOrder } : {}),
    ...(params.from ? { from: params.from } : {}),
    ...(params.to ? { to: params.to } : {}),
    ...(params.q ? { q: params.q } : {}),
    ...(params.adminOnly ? { adminOnly: true } : {}),
  };

  return useQuery<ShopCsMemoLogAllResult | null>(
    ['shop-order-cs-memo', 'log-all', JSON.stringify(base)],
    async () => {
      try {
        return await getShopOrderCsMemoLogAll(base);
      } catch (e) {
        if (isShopCsMemoNotFound(e)) return null;
        throw e;
      }
    },
    { enabled, keepPreviousData: true, staleTime: 60 * 1000, retry: 1 },
  );
};

const patchCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  memo: ShopOrderCsMemo,
  update: (prev: ShopOrderCsMemo[]) => ShopOrderCsMemo[],
) => {
  queryClient.setQueryData<ShopOrderCsMemo[] | null | undefined>(
    shopOrderCsMemoQueryKey(memo.shopOrder),
    (prev) => (Array.isArray(prev) ? update(prev) : prev),
  );
  queryClient.setQueriesData<Record<string, ShopOrderCsMemo[]> | null | undefined>(
    ['shop-order-cs-memo', 'orders'],
    (prev) => {
      if (!prev) return prev;
      const existing = prev[memo.shopOrder];
      if (!existing) return prev;
      return { ...prev, [memo.shopOrder]: update(existing) };
    },
  );
  void queryClient.invalidateQueries(shopOrderCsMemoQueryKey(memo.shopOrder));
  void queryClient.invalidateQueries(['shop-order-cs-memo', 'orders']);
  void queryClient.invalidateQueries(['shop-order-cs-memo', 'log']);
  void queryClient.invalidateQueries(['shop-order-cs-memo', 'log-all']);
};

export const useCreateShopOrderCsMemo = () => {
  const queryClient = useQueryClient();
  return useMutation(createShopOrderCsMemo, {
    onSuccess: (memo) => patchCaches(queryClient, memo, (prev) => [...prev, memo]),
  });
};

export const useDeleteShopOrderCsMemo = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ memo }: { memo: ShopOrderCsMemo }) => deleteShopOrderCsMemo(memo._id),
    {
      onSuccess: (_data, { memo }) =>
        patchCaches(queryClient, memo, (prev) =>
          prev.filter((m) => m._id !== memo._id),
        ),
    },
  );
};
